<template>
  <v-container fluid class="gssync-container pa-0 ma-0">
    <!-- Modern Header Section -->
    <div v-if="showHeader" class="header-section mb-6">
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-google-spreadsheet</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Google Sheets Sync</h1>
          <p class="page-subtitle mb-0">
            Connect and sync your Google Sheets data with GoDial automatically
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            small
            class="mr-2"
            color="rgba(255,255,255,0.2)"
            text-color="white"
          >
            <v-icon small left>mdi-google</v-icon>
            Google Workspace
          </v-chip>
        </div>
      </div>
      <v-row class="feature-pills ma-0">
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-sync</v-icon>
            <span>Auto Sync</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white"
              >mdi-google-spreadsheet</v-icon
            >
            <span>Spreadsheet</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-cloud-sync</v-icon>
            <span>Cloud Sync</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-chart-line</v-icon>
            <span>Data Analysis</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Content Section -->
    <div style="padding: 0 12px;">
      <v-card class="customBorderRadius mb-4 elevation-2">
        <div class="text-center mt-2 mb-2 pt-6">
          <v-icon color="info" size="40">mdi-google-cloud</v-icon>
        </div>

        <v-card-title class="justify-center">
          <h2 class="headline font-weight-bold info--text">
            Activate Google App Script API
          </h2>
        </v-card-title>

        <v-card-text class="text-center px-6 pb-6">
          <p class="body-1 grey--text text--darken-1">
            Before using this integration, please activate the Google App Script
            API by visiting the Google Apps Script Settings page and switching
            it on.
          </p>

          <v-btn
            color="info"
            class="mt-4 px-6"
            rounded
            elevation="2"
            href="https://script.google.com/home/usersettings"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon left>mdi-open-in-new</v-icon>
            Open Google Apps Script Settings
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card class="customBorderRadius mb-4 elevation-2">
        <div class="text-center mt-4 mb-2 pt-4">
          <v-img
            src="https://www.gstatic.com/images/branding/product/2x/sheets_2020q4_48dp.png"
            height="60"
            contain
            class="mx-auto"
          ></v-img>
        </div>

        <v-card-title class="justify-center">
          <h2 class="headline font-weight-bold primary--text">
            Google Sheet Sync Integration
          </h2>
        </v-card-title>

        <v-card-text class="text-center px-6 pb-6">
          <p class="body-1 grey--text text--darken-1">
            Connect your Google account to sync sheets with GoDial. Click the
            button below to authorize and choose a Google Sheet to sync.
          </p>

          <v-btn
            color="primary"
            :loading="loading"
            :disabled="loading"
            class="mt-4 px-6"
            rounded
            elevation="2"
            @click="connectGoogle"
          >
            <v-icon left>mdi-google</v-icon>
            Connect Google Sheets
          </v-btn>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="mt-6"
          ></v-progress-linear>
        </v-card-text>
      </v-card>

      <!-- Sheet Chooser Dialog -->
      <v-dialog
        v-model="sheetChooser"
        persistent
        max-width="550px"
        transition="dialog-bottom-transition"
      >
        <v-card class="customBorderRadius">
          <v-toolbar color="primary" dark flat>
            <v-toolbar-title>Select a Google Sheet</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>

          <v-card-text class="pt-5">
            <div class="text-center mb-4">
              <v-btn
                color="primary"
                class="px-4"
                rounded
                @click="openSheetPicker"
              >
                <v-icon left>mdi-file-document-outline</v-icon>
                Choose Google Sheet
              </v-btn>
            </div>

            <v-divider class="my-3"></v-divider>

            <div v-if="sheetUrl" class="pa-3 rounded-lg grey lighten-4 mt-4">
              <div class="subtitle-1 font-weight-medium mb-2">
                Selected Sheet:
              </div>
              <div class="sheet-url-container pa-2 rounded">
                <v-icon small color="primary" class="mr-2"
                  >mdi-google-spreadsheet</v-icon
                >
                {{ sheetUrl }}
              </div>
            </div>

            <div v-else class="text-center grey--text text--darken-1 my-6 py-4">
              <v-icon large color="grey lighten-1"
                >mdi-file-document-outline</v-icon
              >
              <div class="mt-2">No sheet selected yet</div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="sheetChooser = false">Cancel</v-btn>
            <v-btn
              color="primary"
              :disabled="!sheetUrl"
              :loading="loading"
              class="px-4"
              @click="submitSheetUrl"
            >
              <v-icon left small>mdi-check</v-icon>
              Submit
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Instructions and Tutorial Section at the end -->
      <v-card class="customBorderRadius elevation-2">
        <v-card-title class="justify-center pt-6">
          <h2 class="headline font-weight-bold primary--text">
            Tutorial & Instructions
          </h2>
        </v-card-title>

        <!-- YouTube Tutorial Video -->
        <v-card-text class="text-center px-6 pb-6">
          <p class="body-1 grey--text text--darken-1 mb-4">
            Watch our step-by-step tutorial on how to set up and use the Google
            Sheets integration with GoDial.
          </p>

          <div class="video-container mb-6">
            <iframe
              width="100%"
              height="315"
              src="https://www.youtube.com/embed/qutYgw61JEU?si=iS-vHwrbK5QFycYh"
              title="Google Sheets Integration Tutorial"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>

          <!-- Set Up onEdit Trigger Instructions -->
          <v-divider class="my-6"></v-divider>

          <div class="text-center mt-6">
            <div class="d-flex align-center justify-center mb-4">
              <v-icon color="warning" size="30" class="mr-2"
                >mdi-clock-outline</v-icon
              >
              <h3 class="title font-weight-bold warning--text mb-0">
                Set Up onEdit Trigger
              </h3>
            </div>

            <p class="body-1 grey--text text--darken-1">
              After connecting your Google Sheet, you'll need to set up an
              onEdit trigger to enable automatic syncing when data is edited in
              your sheet.
            </p>

            <v-expansion-panels flat class="mt-4 customBorderRadius">
              <v-expansion-panel class="customBorderRadius">
                <v-expansion-panel-header class="primary--text">
                  <div class="font-weight-medium">
                    How to set up the onEdit trigger
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card
                    flat
                    class="pa-3 mt-2 grey lighten-4 customBorderRadius"
                  >
                    <div class="text-left">
                      <p class="mb-1">
                        <strong>1.</strong> In your Google Sheet, go to
                        <strong>Extensions → Apps Script</strong>
                      </p>
                      <p class="mb-1">
                        <strong>2.</strong> Click the clock icon (<v-icon small
                          >mdi-clock-outline</v-icon
                        >
                        Triggers) in the left sidebar
                      </p>
                      <p class="mb-1">
                        <strong>3.</strong> Click <strong>+ Add Trigger</strong>
                      </p>
                      <p class="mb-1">
                        <strong>4.</strong> Set up the trigger with these
                        settings:
                      </p>
                      <ul class="pl-6 mb-2">
                        <li>
                          Choose which function to run:
                          <code>sendNewRowData</code>
                        </li>
                        <li>Deployment: <code>Head</code></li>
                        <li>Event source: <code>From spreadsheet</code></li>
                        <li>Event type: <code>On edit</code></li>
                      </ul>
                      <p class="mb-1"><strong>5.</strong> Save the trigger</p>
                      <p class="mt-3 font-italic">
                        This trigger is necessary for the automatic sending of
                        data when rows are edited in your Google Sheet.
                      </p>
                    </div>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
const _ = require("lodash");

export default {
  data() {
    return {
      showHeader: false,
      loading: false,
      sheetChooser: false,
      sheetUrl: "",
      authCode: "",
      developerKey: "AIzaSyDyvsAP4DI31z9TtRSrS4YtJslREfOoI40",
      clientId:
        "733503495552-n4b2kd4toqmc6ligf0dj01qo7piegl48.apps.googleusercontent.com",
      pickerApiLoaded: false,
      oauthToken: null,
      scope: [
        "https://www.googleapis.com/auth/drive.file",
        "https://www.googleapis.com/auth/spreadsheets.readonly",
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "teams", "selectedTeam", "fieldList"]),
  },
  mounted() {
    this.showHeader = localStorage.getItem("gsSyncHeaderDismissed") !== "true";
    // Forcefully stay on /integration/googleSheetSync
    const currentPath = window.location.pathname;

    // Now process the code normally
    let code = new URLSearchParams(window.location.search).get("code");

    if (!code && window.location.hash.includes("code=")) {
      const hashParams = new URLSearchParams(
        window.location.hash.split("?")[1],
      );
      code = hashParams.get("code");
    }

    console.log("code in gssync: ", code);

    if (code) {
      this.authCode = code;
      this.sheetChooser = true;
    }

    this.loadPickerScript();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("gsSyncHeaderDismissed", "true");
    },
    async connectGoogle() {
      this.loading = true;
      try {
        const res = await this.$http.get(
          `${this.ENDPOINT}/gsSyncs/getAuthorizeUrl`,
        );
        const data = res.body;
        if (data) {
          window.location.href = data; // Redirect to Google OAuth
        } else {
          console.log("Failed to get Google Auth URL");
        }
      } catch (e) {
        console.log("Error connecting to Google", e);
      } finally {
        this.loading = false;
      }
    },

    loadPickerScript() {
      if (!document.getElementById("gapiScript")) {
        const script = document.createElement("script");
        script.id = "gapiScript";
        script.src = "https://apis.google.com/js/api.js";
        script.onload = () => {
          console.log("Google API loaded");
        };
        document.head.appendChild(script);
      }
    },

    openSheetPicker() {
      const self = this;

      if (!window.gapi) {
        console.error("Google API not loaded");
        return;
      }

      gapi.load("auth", { callback: onAuthApiLoad });
      gapi.load("picker", { callback: onPickerApiLoad });

      function onAuthApiLoad() {
        gapi.auth.authorize(
          {
            client_id: self.clientId,
            scope: self.scope,
            immediate: false,
          },
          handleAuthResult,
        );
      }

      function onPickerApiLoad() {
        self.pickerApiLoaded = true;
        createPicker();
      }

      function handleAuthResult(authResult) {
        if (authResult && !authResult.error) {
          self.oauthToken = authResult.access_token;
          createPicker();
        } else {
          console.error("Auth failed", authResult.error);
        }
      }

      function createPicker() {
        if (self.pickerApiLoaded && self.oauthToken) {
          const view = new google.picker.View(
            google.picker.ViewId.SPREADSHEETS,
          );
          const picker = new google.picker.PickerBuilder()
            .setOAuthToken(self.oauthToken)
            .setDeveloperKey(self.developerKey)
            .addView(view)
            .setCallback(pickerCallback)
            .build();
          picker.setVisible(true);
        }
      }

      function pickerCallback(data) {
        if (data.action == google.picker.Action.PICKED) {
          const fileId = data.docs[0].id;
          self.sheetUrl = `https://docs.google.com/spreadsheets/d/${fileId}/edit`;
        }
      }
    },

    async submitSheetUrl() {
      if (!this.sheetUrl || !this.authCode) return;

      this.loading = true;
      try {
        const res = await this.$http.post(
          `${this.ENDPOINT}/gsSyncs/inject-code`,
          {
            sheetUrl: this.sheetUrl,
            code: this.authCode,
          },
        );
        const data = res.body;

        if (data && !data.error) {
          this.$swal({
            type: "success",
            text: "Google Sheet synced successfully!",
          });
          // this.$toast && this.$toast.success('Google Sheet synced successfully!');
          this.sheetChooser = false;

          // Clean up the URL
          const url = new URL(window.location);
          url.searchParams.delete("code");
          window.history.replaceState({}, document.title, url.pathname);
        } else {
          // this.$toast && this.$toast.error(data.message || 'Failed to sync sheet');
          this.$swal({
            type: "error",
            text: data.message || "Failed to sync sheet",
          });
        }
      } catch (e) {
        // this.$toast && this.$toast.error('Error submitting sheet');
        this.$swal({ type: "error", text: "Error submitting sheet" });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gssync-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .gssync-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 24px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.feature-pills {
  margin-top: 16px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 500;
  height: 100%;
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

p {
  margin: 4px;
  line-height: 1.6;
}

.rounded-lg {
  border-radius: 12px !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.sheet-url-container {
  word-break: break-all;
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.v-card {
  transition: all 0.3s ease;
}
</style>
